import React, { useState, useCallback, useRef, useEffect } from 'react';
import { EditorComposer, Editor, Divider } from '../../editor';
import ToolbarPlugin from '../../editor/plugins/ToolbarPlugin/ToolbarPlugin';
import {
  AlignDropdown,
  BackgroundColorPicker,
  BlockFormatDropdown,
  BoldButton,
  CodeFormatButton,
  CodeLanguageDropdown,
  FloatingLinkEditor,
  FontFamilyDropdown,
  FontSizeDropdown,
  InsertDropdown,
  InsertLinkButton,
  ItalicButton,
  RedoButton,
  TextColorPicker,
  TextFormatDropdown,
  UnderlineButton,
  UndoButton,
} from '../../editor/plugins/ToolbarPlugin/components';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createEquationNode } from '../../editor/nodes/EquationNode';
import { $createImageNode } from '../../editor/nodes/ImageNode';
import { useAuth } from '../../context/AuthContext';
import { createEditor } from 'lexical';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation, splitTextAndMath } from '../utils/latexParser';
import { Modal, Button } from '../common';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// EditorComponent 수정
function EditorComponent({ onLoad, onSave }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // ContentEditable__root 요소를 찾아서 스타일 적용
    setTimeout(() => {
      const editorElement = document.querySelector('div.ContentEditable__root');
      if (editorElement) {
        // 기본 CSS 오버라이드 - 컨텐츠에 맞게 높이 자동 조절
        editorElement.style.padding = '20px';
        editorElement.style.minHeight = '200px';
        editorElement.style.height = 'auto';
        editorElement.style.overflow = 'visible';
        editorElement.style.resize = 'none';
        editorElement.style.maxWidth = 'none';
        editorElement.style.width = '100%';
        editorElement.style.whiteSpace = 'pre-wrap';
        editorElement.style.fontSize = '16px';  // 폰트 크기 줄임
        editorElement.style.fontWeight = '500';  // 문제 미리보기와 동일한 폰트 굵기
      }
      
      // editor-container 스타일 설정
      const editorInnerContainer = document.querySelector('.editor-container');
      if (editorInnerContainer) {
        editorInnerContainer.style.width = '100%';
        editorInnerContainer.style.maxWidth = '465px';
        editorInnerContainer.style.margin = '0 auto';
        editorInnerContainer.style.overflow = 'visible';
        editorInnerContainer.style.height = 'auto';
        editorInnerContainer.style.minHeight = 'auto';
      }
      
      // editor-shell 스타일 오버라이드
      const editorShell = document.querySelector('.editor-shell');
      if (editorShell) {
        editorShell.style.maxWidth = 'none';
        editorShell.style.width = '100%';
        editorShell.style.margin = '0';
        editorShell.style.height = 'auto';
      }
    }, 100);
  }, []);

  // saveEditorState 함수 추가
  const saveEditorState = useCallback(() => {
    const editorState = editor.getEditorState();
    const json = editorState.toJSON();
    onSave(json);
  }, [editor, onSave]);

  const handleMathData = useCallback(async (data) => {
    try {
      console.log("Received data in handleMathData:", data);
      
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        
        let currentParagraph = $createParagraphNode();
        root.append(currentParagraph);

        // 데이터 구조 확인 및 수정
        const linedData = data.mathpix_data?.lined_data;
        if (!linedData) {
          console.error('No lined_data found in:', data);
          return;
        }

        linedData.forEach(item => {
          // chart나 diagram 타입 또는 text/math/table이 아닌 타입 처리
          if ((item.type !== 'text' && item.type !== 'math' && item.type !== 'table') || 
              item.type === 'chart' || 
              item.type === 'diagram') {
            // 이미지 데이터 처리
            if (item.content && item.content[0] && 
                (item.content[0].startsWith('data:image') || item.is_base64)) {
              // console.log('Creating image node with:', item.content[0]); // 디버깅용
              const imageNode = $createImageNode({
                altText: 'Problem Image',
                src: item.content[0],
                width: item.cnt ? Math.abs(item.cnt[2][0] - item.cnt[0][0]) : undefined,
                height: item.cnt ? Math.abs(item.cnt[2][1] - item.cnt[0][1]) : undefined,
                maxWidth: 500,
                showCaption: false
              });
              currentParagraph.append(imageNode);
              
              // 이미지 다음에 새 단락 추가
              currentParagraph = $createParagraphNode();
              root.append(currentParagraph);
            }
          } else if (item.type === 'text') {
            item.content.forEach(text => {
              // 수식과 텍스트를 분리
              const mathSegments = splitTextAndMath(text);
              mathSegments.forEach(mathSegment => {
                if (mathSegment.type === 'text') {
                  // 일반 텍스트에서 (숫자) 패턴을 찾아 변환
                  const segments = mathSegment.content.split(/(\(\d+\))/);
                  segments.forEach(segment => {
                    const numberMatch = segment.match(/\((\d+)\)/);
                    if (numberMatch) {
                      const num = parseInt(numberMatch[1]);
                      if (num >= 1 && num <= 5) {
                        const circleNumber = ['①', '②', '③', '④', '⑤'][num - 1];
                        const equationNode = $createEquationNode(circleNumber, true);
                        currentParagraph.append(equationNode);
                      } else {
                        const textNode = $createTextNode(segment);
                        currentParagraph.append(textNode);
                      }
                    } else {
                      const textNode = $createTextNode(segment);
                      currentParagraph.append(textNode);
                    }
                  });
                } else { // math
                  const equation = processMathEquation(mathSegment.content);
                  const equationNode = $createEquationNode(equation, true);
                  currentParagraph.append(equationNode);
                }
              });
          });
          
          // 텍스트 다음에 새 단락 추가
          currentParagraph = $createParagraphNode();
          root.append(currentParagraph);
        } else if (item.type === 'math' || item.type === 'table') {
          // 기존 수식/테이블 처리 로직 유지
          const mathContent = item.content[0];
          let equation = mathContent.replace(/^\$\$(.*)\$\$/s, '$1');
          
          if (item.type === 'table') {
            equation = equation
              .replace(/\\begin\{tabular\}/g, '\\begin{array}')
              .replace(/\\end\{tabular\}/g, '\\end{array}')
              .replace(/\$/g, ' ')
              .replace(/\\hline(?!\s)/g, '\\hline ');
          }
          
          equation = processMathEquation(equation);
          const isDisplayMath = mathContent.startsWith('$$');
          const equationNode = $createEquationNode(equation, !isDisplayMath);
          currentParagraph.append(equationNode);
          
          currentParagraph = $createParagraphNode();
          root.append(currentParagraph);
        }
      });
    });
  } catch (error) {
    console.error('Math data handling error:', error);
    console.error('Error details:', error.message);
  }
}, [editor]);

  useEffect(() => {
    if (onLoad) {
      onLoad(handleMathData);
    }
  }, [onLoad, handleMathData]);

  return (
    <div className="flex flex-col">
      <div className="editor-wrapper">
        <Editor>
          <ToolbarPlugin>
            {/* <FontFamilyDropdown /> */}
            {/* <FontSizeDropdown /> */}
            {/* <Divider /> */}
            {/* <BoldButton /> */}
            {/* <ItalicButton /> */}
            {/* <UnderlineButton /> */}
            {/* <CodeFormatButton /> */}
            {/* <InsertLinkButton /> */}
            {/* <TextColorPicker /> */}
            {/* <BackgroundColorPicker /> */}
            <TextFormatDropdown />
            <Divider />
            <InsertDropdown />
            <Divider />
            <AlignDropdown />
          </ToolbarPlugin>
        </Editor>
      </div>
      <div className="border-t py-2 px-2 flex justify-end items-center">
        <Button
          onClick={saveEditorState}
          variant="primary"
          className="bg-green-500 hover:bg-green-600"
          disabled={!editor}
        >
          문제 저장
        </Button>
      </div>
    </div>
  );
}

function ProblemEditModal({ problem, onClose, onUpdate }) {
  const { getAccessToken } = useAuth();
  const [labels, setLabels] = useState(problem.problem_info.labels);
  const [imageUrl, setImageUrl] = useState(problem.base64format);
  const [isLoading, setIsLoading] = useState(false);
  const canvasRef = useRef(null);
  const mathDataHandlerRef = useRef(null);
  const isMobileView = window.innerWidth < 768;
  const [imageScale, setImageScale] = useState(1);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // 상태에 따른 초기 에디터 상태 설정
  const getInitialEditorState = useCallback(() => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes],
      theme: {
        paragraph: 'editor-paragraph',
        text: {
          bold: 'editor-text-bold',
          italic: 'editor-text-italic',
          underline: 'editor-text-underline',
        },
      }
    });
    
    if (problem.lexical_data?.lexical_data && 
        typeof problem.lexical_data.lexical_data === 'object') {
      try {
        const lexicalData = JSON.parse(JSON.stringify(problem.lexical_data.lexical_data));
        
        if (!lexicalData.root) {
          return createEmptyEditorState(editor);
        }

        // 모든 노드에 대해 처리를 수행하는 재귀 함수
        const processNode = (node) => {
          // 기본 속성 초기화
          node.style = node.style || "";
          node.format = node.format || "";
          node.indent = node.indent || 0;
          node.direction = node.direction || null;
          node.version = node.version || 1;

          // equation 타입 노드 처리
          if (node.type === 'equation') {
            node.equation = processMathEquation(node.equation);
          }

          // children이 있는 경우 재귀적으로 처리
          if (node.children && Array.isArray(node.children)) {
            node.children = node.children.map(child => processNode(child));
          }

          return node;
        };

        // root 노드부터 시작하여 모든 노드 처리
        lexicalData.root = processNode(lexicalData.root);

        const editorState = editor.parseEditorState(lexicalData);
        return editorState;
        
      } catch (error) {
        console.error('Error parsing editor state:', error);
        return createEmptyEditorState(editor);
      }
    }
    
    return createEmptyEditorState(editor);
  }, [problem]);

  // 빈 에디터 상태 생성 함수
  const createEmptyEditorState = (editor) => {
    return editor.parseEditorState({
      root: {
        children: [
          {
            children: [],
            direction: null,
            format: "",
            indent: 0,
            type: "paragraph",
            version: 1,
            style: ""
          }
        ],
        direction: null,
        format: "",
        indent: 0,
        type: "root",
        version: 1,
        style: ""
      }
    });
  };

  // 테두리 그리기 함수
  const drawBoundingBoxes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl || !problem.mathpix_data) return;

    const img = new Image();
    img.src = imageUrl;
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      ctx.drawImage(img, 0, 0, img.width, img.height);
      
      problem.mathpix_data.lined_data.forEach(item => {
        if (item.cnt && item.cnt.length === 4) {
          ctx.strokeStyle = 'red';
          ctx.lineWidth = 2;
          
          const [[x1, y1], [x2, y2], [x3, y3], [x4, y4]] = item.cnt;
          
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.lineTo(x3, y3);
          ctx.lineTo(x4, y4);
          ctx.closePath();
          ctx.stroke();
        }
      });
    };
  }, [imageUrl, problem.mathpix_data]);

  // 컴포넌트 마운트 시 테두리 그리기
  useEffect(() => {
    if (problem.problem_info.status >= 300) {
      drawBoundingBoxes();
    }
  }, [drawBoundingBoxes, problem.problem_info.status]);

  // mathpix 데이터를 에디터에 반영
  const setMathDataHandler = useCallback((handler) => {
    mathDataHandlerRef.current = handler;
    if (problem.problem_info.status === 300 && problem.mathpix_data) {
      handler(problem.mathpix_data);
    }
  }, [problem]);

  // 저장 처리 함수 수정
  const handleSave = async (lexicalJson) => {
    try {
      const response = await fetchWithTokenRefresh(`/api/problems/${problem.problem_id}/ocr/confirm`, {
        method: 'POST',
        body: JSON.stringify({
          lexical_json: lexicalJson,
          labels: labels
        })
      });

      if (!response || (response.status_code !== 200 && response.status_code !== 201)) {
        throw new Error('에디터 데이터 저장 실패');
      }

      console.log('Editor data saved successfully');
      onUpdate(); // 현재 페이지 데이터 업데이트
      onClose();
    } catch (error) {
      console.error('에디터 데이터 저장 중 오류:', error);
      alert('문제 수정에 실패했습니다.');
    }
  };

  // fetchWithTokenRefresh 함수 추가
  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('API 요청 실패');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API 호출 에러:', error);
      throw error;
    }
  }, [getAccessToken]);

  // OCR 처리 함수 수정
  const handleOcr = useCallback(async () => {
    if (!problem.problem_id || !mathDataHandlerRef.current) return;

    setIsLoading(true);
    try {
      const response = await fetchWithTokenRefresh(`/api/problems/${problem.problem_id}/ocr`, {
        method: 'POST',
        body: JSON.stringify({
          mathpix_data: true
        })
      });

      if (!response || response.status_code !== 201) {
        throw new Error('OCR 처리 실패');
      }

      // 테두리 그리기
      drawBoundingBoxes(response.data);
      
      // OCR 데이터 처리
      await mathDataHandlerRef.current(response.data);
      
      // 상태 업데이트를 위해 onUpdate 호출
      onUpdate();
      
      alert('OCR 처리가 완료되었습니다.');
    } catch (error) {
      console.error('OCR 처리 중 오류:', error);
      alert('OCR 처리에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [problem.problem_id, fetchWithTokenRefresh, drawBoundingBoxes, onUpdate]);

  // OCR 데이터 적용 함수 추가
  const handleApplyOCR = useCallback(async () => {
    if (!problem.problem_id || !mathDataHandlerRef.current || !problem.mathpix_data) return;

    setIsLoading(true);
    try {
      // 기존 OCR 데이터 적용
      await mathDataHandlerRef.current(problem);
      drawBoundingBoxes(problem);
      alert('OCR 데이터가 적용되었습니다.');
    } catch (error) {
      console.error('OCR 데이터 적용 중 오류:', error);
      alert('OCR 데이터 적용에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [problem, drawBoundingBoxes]);

  // 모달 닫기 전 확인
  const handleCloseModal = useCallback(() => {
    setShowCloseConfirm(true);
  }, []);

  // 확인 후 모달 닫기
  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    onClose();
  }, [onClose]);

  // 취소
  const cancelClose = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  return (
    <>
    <Modal
      isOpen={true}
      onClose={handleCloseModal}
      title={
        <div className="flex items-baseline gap-2">
          <span>문제 수정</span>
          <span className="text-sm text-gray-500">- PID: {problem.problem_id}</span>
        </div>
      }
      size="full"
      className="flex flex-col"
      bodyClassName="flex flex-col flex-1 overflow-auto p-4"
      zIndex={9999800}
    >
      <div className="flex flex-col">
        {/* 상단: 이미지 영역 */}
        <div className="mb-4">
          <div className="space-y-2">
            {/* 이미지 컨테이너 */}
            <div className="relative overflow-auto rounded-lg border border-gray-200 bg-gray-50">
              <div 
                className="relative inline-block min-w-full p-4"
                style={{ transform: `scale(${imageScale})`, transformOrigin: 'top left' }}
              >
                <img 
                  src={imageUrl} 
                  alt="Problem" 
                  className="size-auto max-w-full"
                />
                {problem.problem_info.status >= 300 && (
                  <canvas
                    ref={canvasRef}
                    className="pointer-events-none absolute left-0 top-0"
                  />
                )}
              </div>
            </div>
            
            {/* 확대/축소 컨트롤 */}
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setImageScale(Math.max(0.5, imageScale - 0.1))}
                className="rounded-full border border-gray-300 p-1 hover:bg-gray-100"
                title="축소"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="min-w-[60px] text-center text-sm font-medium">
                {Math.round(imageScale * 100)}%
              </span>
              <button
                onClick={() => setImageScale(Math.min(2, imageScale + 0.1))}
                className="rounded-full border border-gray-300 p-1 hover:bg-gray-100"
                title="확대"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <button
                onClick={() => setImageScale(1)}
                className="ml-2 rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100"
                title="원본 크기"
              >
                원본
              </button>
            </div>
          </div>

          <div className="mt-3">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              라벨
            </label>
            <div className="rounded-lg bg-gray-100 px-3 py-2 text-sm">
              {labels || '라벨 없음'}
            </div>
          </div>

          {problem.problem_info.status === 200 && (
            <Button
              onClick={handleOcr}
              variant="primary"
              className="mt-4"
              loading={isLoading}
            >
              {isLoading ? '처리 중...' : 'OCR 처리 시작'}
            </Button>
          )}

          {problem.problem_info.status === 300 && (
            <Button
              onClick={handleApplyOCR}
              variant="primary"
              className="mt-4 bg-green-500 hover:bg-green-600"
              loading={isLoading}
            >
              {isLoading ? '처리 중...' : 'OCR 데이터 적용'}
            </Button>
          )}
        </div>

        {/* 하단: 에디터 영역 */}
        <div className="mb-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">수식 편집기</h3>
            <div className="text-sm text-gray-500">
              문제 내용을 편집하세요
            </div>
          </div>
          <div className="rounded-lg border border-gray-300 bg-white shadow-sm">
            <div className="w-full [&_.editor-container]:!font-inherit [&_div.editor-equation]:block [&_span.editor-equation]:inline-block">
              <EditorComposer initialEditorState={getInitialEditorState()}>
                <EditorComponent 
                  onLoad={setMathDataHandler} 
                  onSave={handleSave}
                />
              </EditorComposer>
            </div>
          </div>
        </div>
      </div>
    </Modal>
    
    {/* 닫기 확인 팝업 */}
    {showCloseConfirm && (
      <Modal
        isOpen={showCloseConfirm}
        onClose={cancelClose}
        title="확인"
        size="small"
        zIndex={9999900}
      >
        <div className="p-4">
          <p className="mb-4">
            수정한 내용이 저장되지 않았습니다.<br />
            정말로 닫으시겠습니까?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              onClick={cancelClose}
              variant="secondary"
              className="px-4 py-2"
            >
              취소
            </Button>
            <Button
              onClick={confirmClose}
              variant="primary"
              className="bg-red-500 hover:bg-red-600 px-4 py-2"
            >
              닫기
            </Button>
          </div>
        </div>
      </Modal>
    )}
    </>
  );
}

export default ProblemEditModal; 